import React, { useState, useEffect, useCallback } from "react";
import { bindActionCreators } from "redux";
import actions from "../../actions";
import { connect } from "react-redux";
import { getAllSubscriptions } from "../../apis";
import { toast } from "react-toastify";
import Pagination from "../../components/Pagination/Pagination";
import { RxCrossCircled } from "react-icons/rx/index.esm";
import { useNavigate, useLocation } from "react-router-dom";

const SEARCH_ROWS = 100;
const STATUSES = ["active", "cancelled", "billing_issue", "paused", "expired"];

const Subscriptions = ({
  logoutRequest,
  startLoadingRequest,
  stopLoadingRequest,
}) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(null);

  // The URL is the single source of truth for page, search and filters
  const params = new URLSearchParams(search);
  const searchParam = params.get("search") || "";
  const statusParam = params.get("status") || "";
  const productParam = params.get("product") || "";
  const pageParam = parseInt(params.get("page") || "1", 10);
  const rowsPerPage = searchParam ? SEARCH_ROWS : parseInt(params.get("rowsPerPage") || "10", 10);

  const [searchValue, setSearchValue] = useState(searchParam);
  const [productValue, setProductValue] = useState(productParam);

  const updateUrl = useCallback(
    (next) => {
      const merged = { page: 1, rowsPerPage: 10, search: searchParam, status: statusParam, product: productParam, ...next };
      const qs = new URLSearchParams();
      qs.set("page", merged.page);
      qs.set("rowsPerPage", merged.search ? SEARCH_ROWS : merged.rowsPerPage);
      ["search", "status", "product"].forEach((key) => {
        if (merged[key]) qs.set(key, merged[key]);
      });
      navigate(`/subscriptions?${qs.toString()}`, { replace: true });
    },
    [navigate, searchParam, statusParam, productParam]
  );

  const fetch = useCallback(
    async (page, rows, query, status, product) => {
      try {
        startLoadingRequest();
        const apiResponse = await getAllSubscriptions(page, rows, query, status, product);
        if (apiResponse?.data?.data?.length > 0) {
          setData(apiResponse?.data?.data);
          setTotal(apiResponse?.data?.totalCount);
        } else {
          setData([]);
          setTotal(0);
        }
        stopLoadingRequest();
      } catch (error) {
        stopLoadingRequest();
        if (error?.response?.status === 401) {
          toast.error(error.response.data.message);
          logoutRequest();
        } else if (error?.response?.status === 403) {
          toast.error(error.response.data.message);
        }
      }
    },
    [logoutRequest, startLoadingRequest, stopLoadingRequest]
  );

  // Debounce typed search/product text into the URL, resetting to page 1
  useEffect(() => {
    const handler = setTimeout(() => {
      const nextSearch = searchValue.trim();
      const nextProduct = productValue.trim();
      if (nextSearch !== searchParam || nextProduct !== productParam) {
        updateUrl({ page: 1, search: nextSearch, product: nextProduct });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchValue, productValue, searchParam, productParam, updateUrl]);

  // Fetch whenever the URL state changes
  useEffect(() => {
    fetch(pageParam, rowsPerPage, searchParam, statusParam, productParam);
  }, [fetch, pageParam, rowsPerPage, searchParam, statusParam, productParam]);

  const searchChangeHandler = useCallback((event) => {
    setSearchValue(event.target.value);
  }, []);

  const resetSearchHandler = useCallback(() => {
    setSearchValue("");
  }, []);

  const statusChangeHandler = (event) => {
    updateUrl({ page: 1, status: event.target.value });
  };

  function formatPrice(amount, currency) {
    if (typeof amount !== "number") return "-";
    return `${currency || ""} ${amount.toFixed(2)}`.trim();
  }

  function formatDateTime(value) {
    if (!value) return "-";
    return new Date(value).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" });
  }

  function formatDate(ms) {
    if (!ms) return "-";
    const date = new Date(ms);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const handlePageChange = (newPage) => {
    updateUrl({ page: newPage, rowsPerPage });
  };

  return (
    <div className="section">
      <h1>Subscriptions</h1>
      <div className="wrapper">
        <div className="table-wrapper">
          <div className="table-header flex-start">
            <div className="input-wrapper">
              <select value={statusParam} onChange={statusChangeHandler}>
                <option value="">All</option>
                {STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>
            <div className="search">
              <div className="input-wrapper">
                <input
                  value={productValue}
                  onChange={(event) => setProductValue(event.target.value)}
                  placeholder="Product ID (exact)"
                />
              </div>
            </div>
            <div className="search">
              <div className="input-wrapper">
                <input
                  value={searchValue}
                  onChange={searchChangeHandler}
                  placeholder="Search..."
                />
                {searchValue?.trim() && (
                  <RxCrossCircled onClick={resetSearchHandler} size={18} />
                )}
              </div>
            </div>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th className="px-4 py-3">S.No</th>
                  <th className="px-4 py-3">Username</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Start Date</th>
                  <th className="px-4 py-3">End Date</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Store</th>
                  <th className="px-4 py-3">Country</th>
                  <th className="px-4 py-3">Created At</th>
                  <th className="px-4 py-3">Updated At</th>
                </tr>
              </thead>
              <tbody>
                {data?.map((item, index) => (
                  <tr
                    key={item._id}
                    // style={{ cursor: "pointer" }}
                  >
                    <td>{(pageParam - 1) * rowsPerPage + index + 1}</td>
                    <td>{item?.userId?.username}</td>
                    <td>{item?.product_id}</td>
                    <td>{formatPrice(item?.price_in_purchased_currency, item?.currency)}</td>
                    <td>{item?.type}</td>
                    <td>{formatDate(item?.purchased_at_ms)}</td>
                    <td>{formatDate(item?.expiration_at_ms)}</td>
                    <td>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "500",
                          backgroundColor: item?.status === "active" ? "#d1fae5" : "#fee2e2",
                          color: item?.status === "active" ? "#065f46" : "#991b1b",
                          textTransform: "capitalize",
                        }}
                      >
                        {item?.status?.replace("_", " ")}
                      </span>
                    </td>
                    <td>{item?.store}</td>
                    <td>{item?.country_code}</td>
                    <td>{formatDateTime(item?.createdAt)}</td>
                    <td>{formatDateTime(item?.updatedAt)}</td>
                  </tr>
                ))}
                {data?.length === 0 && (
                  <tr>
                    <td colSpan={12} className="px-4 py-6 text-center text-gray-400">
                      No subscriptions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {data?.length > 0 && (
            <Pagination
              pageInfo={{
                page: pageParam,
                total: Math.ceil(total / rowsPerPage),
                totaluser:total,
                rowsPerPage,
              }}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      logoutRequest: actions.auth.logout,
      stopLoadingRequest: actions.loader.stopLoading,
      startLoadingRequest: actions.loader.startLoading,
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(Subscriptions);